/** @module csi/page/LoginForm */
define([
	"require",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom",
	"dojo/dom-attr",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/hash",
	"dojo/io-query",
	"dojo/on",
	"dijit",
	"dijit/registry",
	"csi",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/comm/GetRequest",
	"csi/comm/PostRequest",
	"csi/keycloak/KeycloakAdapter",
	"csi/utility/routerUtility",
	"csi/utility/stringUtility",
	"csi/widget/FormDeclaration",
	"csi/widget/InvalidMessage",
	"dojo/text!csi/page/LoginFormField.html",
	"dijit/form/_TextBoxMixin" // dijit.selectInputText
], function(require, declare, lang, dom, domAttr, domClass, domConstruct, hash, ioQuery, on, dijit, registry,
	csi, _, html, locale, GetRequest, PostRequest, KeycloakAdapter, routerUtil, {stringSubstitute}, FormDeclaration, InvalidMessage, fieldTemplate) {

	var FIELD_SECTION_SUFFIX = "SectionNode";

	var ERROR_MESSAGE_STYLE = "errorMsg";
	var INFO_MESSAGE_STYLE = "infoMsg";
	var NO_MESSAGE_STYLE = "noMsg";
	var ALL_MESSAGE_STYLES = [ERROR_MESSAGE_STYLE, INFO_MESSAGE_STYLE, NO_MESSAGE_STYLE];

	var LOGIN_ID = "LoginID";
	var LOGIN_BUTTON_LABEL = "loginButton_label";
	const ORIGINAL_HEIGHT = 458;
	const MINI_HEIGHT = 328;

	function showPageContent() {
		html.setVisibility(html.pageContent(), true);
	}

	return declare(FormDeclaration, {
		// DOMNode
		loginPane: null,
		msgContainerNode: null,
		messageNode: null,
		backButton: null,
		loginButton: null,
		loginTitleSpan: null,
		forgotPasswordLink: null,
		forgotPasswordMessage: null,


		// DOMNode
		loginNameSectionNode: null,

		// CFCA DOMNode
		CFCASignatureNode: null,

		// DOMNode
		authenticationFieldSectionNode: null,

		// from data-dojo-props
		reauthentication: null,

		// String[]
		fieldIds: null,

		// AuthenticationProvider[]
		authenticationProviders: null,

		// boolean
		twoStepAuthentication: false,
		forgotPassword: false,

		startup: function() {
			// summary: initial setup of form, and query for authentication providers
			this.loginPane = dom.byId("loginPane");
			this.backButton = registry.byId("backButton");
			this.loginButton = registry.byId("loginButton");
			this.messageNode = registry.byId("messageNode");
			this.loginTitleSpan = dom.byId("loginTitleSpan");
			this.forgotPasswordLink = dom.byId("forgotPasswordLink");
			this.forgotPasswordMessage = dom.byId("forgotPasswordMessage");

			on(this.backButton, 'click', lang.hitch(this, "_onClickBackBtn"));
			on(this.forgotPasswordLink, 'click', lang.hitch(this, "_onClickForgotPasswordLink"));

			html.setVisible(this.backButton, false);
			domClass.add(this.msgContainerNode, NO_MESSAGE_STYLE);

			this.fieldIds = [];

			KeycloakAdapter.setKeycloakEnabled(false);
			new PostRequest("DataSource", "GetAuthenticationProviders").post({
				load: lang.hitch(this, this._onLoadAuthenticationProviders),
				error: lang.hitch(this, this._onInitError),
				retryCount: 1,
				timeout: 60000
			});
		},

		initialize: async function(authenticationProviders) {
			this.forgotPassword = false;
			var params = routerUtil.decomposeHash();
			if (!(params.redirect == 0 || params.Redirect == 0)) {
				const request = new GetRequest("DataSource", "IsKeycloakInstalled");
				const requestKeycloakParams = new GetRequest("DataSource", "GetKeycloakParams");
				try {
					const response = await request.get();
					const keycloakParamsResponse = await requestKeycloakParams.get();
					if (response.getReturnValue()) {
						KeycloakAdapter.setKeycloakEnabled(true);
						const params = keycloakParamsResponse.getReturnValue();
						KeycloakAdapter.setKeycloakParams(params.url, params.realm, params.clientId, params.redirectUri, params.tokenRefresh, locale.getLocale(), params.identityProviderHint);
						const url = window.location.href;
						const prevLocation = url.replace((window.location.origin + "/WebAccess/login.html"), "");
						KeycloakAdapter.login(prevLocation);
					}
				} catch (ex) {
					this._showError(ex);
				}
			}
			this.authenticationProviders = authenticationProviders;

			this.twoStepAuthentication = _.some(authenticationProviders, function(provider) {
				return provider.setUpPerUser;
			});

			var formHeight = ORIGINAL_HEIGHT;
			if (this.twoStepAuthentication) {
				formHeight = MINI_HEIGHT;
				this.setLoginButtonLabel("Next");
				html.setVisible(this.forgotPasswordLink, false);
			}
			html.setPixels(this.loginPane, "height", formHeight);

			this.createFields();

			// if there is a hash location, then the session has expired and user needs to login again
			if (hash() && !this.reauthentication) {
				if (!(params.redirect == 0 || params.Redirect == 0)) {
					window.sessionStorage.removeItem('centricdrive_token_key');
					this._refresh(locale.resource("SESSION_EXPIRED"), ERROR_MESSAGE_STYLE);
				}
			} else if (this.redirectError) {
				// Display any errors due to redirection (e.g. SSO login error)
				this._showRedirectError();
			} else {
				this._refresh();
			}

			showPageContent();

			// set focus to login field
			setTimeout(function() {
				dijit.selectInputText(LOGIN_ID);
				csi.markAsProgress(false);
			}, 1);
		},

		createFields: function() {
			_.each(this.authenticationProviders, function(provider) {
				_.each(provider.getFieldDefinitions(), function(fieldDef) {
					var id = fieldDef.id;
					var inputType = fieldDef.type || "password";
					var label = fieldDef.label || locale.resource(id);
					var value = fieldDef.value || "";
					var autocomplete = fieldDef.autocomplete;
					var onclick = fieldDef.onClick;
					var readOnly = fieldDef.readOnly;

					this.fieldIds.push(id);

					var htmlStr = stringSubstitute(fieldTemplate, [id, inputType, label, value, provider.type]).trim();

					var node = domConstruct.toDom(htmlStr, this.ownerDocument);

					if (id == "CFCASignature") {
						this.CFCASignatureNode = node;
					}

					html.setVisible(node, !this.twoStepAuthentication);

					if (id == "CFCASignButton") {
						//update the CFCA sign in button text to "Select Login Certificate" and place it inside CFCASignatureNode so it can be placed easily using css
						node.lastElementChild.value = locale.resource("LoginSelectCertificate");
						domClass.add(node.lastElementChild, "csi-secondary-btn");
						this.CFCASignatureNode.appendChild(node);
					} else {
						this.authenticationFieldSectionNode.appendChild(node);
					}

					var inputFieldNode = dom.byId(id);
					if (autocomplete) {
						domAttr.set(inputFieldNode, "autocomplete", "on");
					}
					if (readOnly) {
						domAttr.set(inputFieldNode, "readOnly", true);
					}
					if (onclick) {
						on(inputFieldNode, "click", lang.hitch(this, this._onClickField, onclick));
					}
				}, this);
			}, this);
		},

		_onClickBackBtn: function() {
			// summary: login Back button action
			if (this.forgotPassword) {
				// back button when in the forgot password form
				this._endForgotPassword();
				html.setPixels(this.loginPane, "height", ORIGINAL_HEIGHT);
			} else if (this.twoStepAuthentication) {
				this.setLoginButtonLabel("Next");
				html.setVisible(this.forgotPasswordLink, false);
				html.setVisible(this.loginNameSectionNode, true);
				html.setVisible(this.backButton, false);
				this._showAuthenticationFields();
				html.setPixels(this.loginPane, "height", MINI_HEIGHT);
			}
		},

		_initForgotPassword: function() {

			domAttr.set(this.loginTitleSpan, "textContent", locale.resource("ForgotPassword"));
			this.setLoginButtonLabel("SendPassword");
			domAttr.set(this.forgotPasswordMessage, "textContent", locale.resource("ForgotPasswordInfo"));
			html.setVisible(this.forgotPasswordMessage, true);
			html.setVisible(this.forgotPasswordLink, false);
			html.setVisible(this.backButton, true);
			html.setVisible(this.loginNameSectionNode, true);
			domClass.replace(this.msgContainerNode, NO_MESSAGE_STYLE, ALL_MESSAGE_STYLES);
			this._showAuthenticationFields();
			this.forgotPassword = true;
		},

		_endForgotPassword: function(submitted) {
			// put back the login form elements
			domAttr.set(this.loginTitleSpan, "textContent", locale.resource("LoginFormTitle"));
			domClass.replace(this.msgContainerNode, NO_MESSAGE_STYLE, ALL_MESSAGE_STYLES);

			if (submitted) {
				html.setVisible(this.forgotPasswordMessage, true);
				domAttr.set(this.forgotPasswordMessage, "textContent", locale.resource("ForgotPasswordSent"));
			} else {
				html.setVisible(this.forgotPasswordMessage, false);
			}

			var twoSteps = this.twoStepAuthentication ? true : false;
			this.setLoginButtonLabel("LoginButton");
			html.setVisible(this.forgotPasswordLink, true);
			html.setVisible(this.backButton, twoSteps);
			html.setVisible(this.loginNameSectionNode, !twoSteps);
			html.setVisible(this.messageNode, false);
			this._showAuthenticationFields(true);
			this.forgotPassword = false;
		},

		_onClickForgotPasswordLink: function() {
			this._initForgotPassword();
		},

		_showAuthenticationFields: function(show) {
			if (this.twoStepAuthentication) {
				show = this.forgotPassword;
			}
			_.each(this.authenticationFieldSectionNode.childNodes, function(childNodes) {
				html.setVisible(childNodes, show);
			});
		},

		_onClickField: function( /*Function*/ callback, /*Event*/ event) {
			try {
				callback(event);
			} catch (ex) {
				this._refresh(ex.message, ERROR_MESSAGE_STYLE);
			}
		},

		onSubmit: function( /*Event*/ e) {
			// summary: depending on the state of the visibility of loginNameSectionNode
			// either ask the server about the login type or submit a real login info

			var loginNameVisible = html.isVisible(this.loginNameSectionNode);
			if (this.twoStepAuthentication && loginNameVisible) {
				this.askLoginType();

			} else if (this.forgotPassword) {
				this.submitForgotPassword();
			} else {
				// reset message states
				html.setVisible(this.forgotPasswordMessage, false);
				domClass.replace(this.msgContainerNode, NO_MESSAGE_STYLE, ALL_MESSAGE_STYLES);

				this.performLogin();

			}

			e.preventDefault();
		},

		askLoginType: function() {
			new PostRequest("DataSource", "GetLoginType", {
				LoginID: dom.byId(LOGIN_ID).value
			}).post({
				load: lang.hitch(this, this._onLoadLoginType),
				error: lang.hitch(this, this._onError),
				timeout: 60000
			});
		},

		submitForgotPassword: function() {
			var username = dom.byId(LOGIN_ID).value

			if (username.trim().length < 1) {
				this._showError({message: locale.resource("EnterUsernamePrompt")});
				return;
			}

			new PostRequest("SiteAdmin", "SendPasswordResetEmail", {
				username: username,
				manualSet: false
			}).post({
				load: lang.hitch(this, this._endForgotPassword),
				error: lang.hitch(this, this._showError),
				timeout: 60000
			});

		},

		performLogin: function() {
			// summary: submit login request and if successful switch to homepage
			this._refresh(locale.resource("LoginStatus"), INFO_MESSAGE_STYLE, true);

			var formValues = this._getFormValues();

			try {
				this._preLogin(formValues);
				new PostRequest("DataSource", "SimpleLogin", formValues).post({
					load: lang.hitch(this, this._onLoad),
					error: lang.hitch(this, this._onError),
					timeout: 60000
				});
			} catch (e) {
				this._refresh(e.message, ERROR_MESSAGE_STYLE);
			}
		},

		/**
		 * Temporary solution while we wait for the SSO mechanism to be ready.
		 */
		performLoginToCentricDrive: function() {
			if (this._getFormValues().LoginID != "Administrator") {
				try {
					return new PostRequest("SiteAdmin", "PerformLoginToCentricDrive")
						.addParameter("UserName", this._getFormValues().LoginID)
						.addParameter("Password", this._getFormValues().Password)
						.post({timeout: 60000});
				} catch (e) {
					this._refresh(e.message, ERROR_MESSAGE_STYLE);
				}
			}
			return Promise.resolve();
		},

		_getFormValues: function() {
			var formValues = {};
			_.each(this.getFormFields(), function(node) {
				if (node && node.id)
					formValues[node.id] = node.value;
			});
			return formValues;
		},

		_onLoadAuthenticationProviders: function(response) {
			var self = this;

			var providerNodes = response.getResultNodes();

			// load AMD modules before proceeding
			var amdModuleNames = _.pluck(providerNodes, "AMDModuleName");
			require(amdModuleNames, function() {
				// each argument to this function will be a class derived from 'csi/authentication/AuthenticationProvider'
				var providers = [];
				for (var i = 0; i < arguments.length; i++) {
					var node = providerNodes[i];
					var props = {
						type: node.Type,
						setUpPerUser: node.SetUpPerUser
					};

					// create an instance of AuthenticationProvider supplying some its properties from a server
					providers.push(new arguments[i](props));
				}

				// now we are ready to initialize a login form
				self.initialize(providers);
			});
		},

		_onLoadLoginType: function(response) {
			// summary: hide login name and show authentication fields depending on the response
			var loginTypes = [];
			var loginTypeNode = response.getResultNode();
			if (loginTypeNode) {
				loginTypes = loginTypeNode.AuthenticationTypes;
			}

			html.setVisible(this.loginNameSectionNode, false);
			html.setVisible(this.backButton, true);
			this.setLoginButtonLabel("LoginButton");
			html.setVisible(this.messageNode, false);
			html.setVisible(this.forgotPasswordLink, true);
			_.each(this.fieldIds, function(fieldId) {
				var sectionId = fieldId + FIELD_SECTION_SUFFIX;
				var sectionNode = dom.byId(sectionId);
				if (!sectionNode) {
					console.error("Cannot find section with id=" + sectionId);
					return;
				}

				var authenticationType = sectionNode.getAttribute("data-csi-authentication");
				var sectionVisible = _.contains(loginTypes, authenticationType);

				html.setVisible(sectionNode, sectionVisible);
			});
		},
		_onLoad: function(response) {
			// summary: set the user locale to the localStorage/cookie and redirect to the home page
			self = this;
			var session = response.getResultNode();
			if (session) {
				var localeName = session.UserLocale;
				if (localeName) {
					locale.storeLocale(localeName);
				}
			}

			if (response.data.PasswordStatus == "EXPIRED") {
				document.location.replace(csi.url("resetPassword.html") + "?LoginId=" + encodeURIComponent(response.data.ID) + "&state=expired");
			} else if (response.data.PasswordStatus == "NEARLY_EXPIRED") {
				document.location.replace(csi.url("resetPassword.html") + "?URL=" + encodeURIComponent(response.data.ID) + "&state=nearlyExpired");
			} else {

				this.performLoginToCentricDrive().then(function(response) {
					if (response) {
						window.sessionStorage.setItem('centricdrive_token_key', response);
					}
				}).catch(error => {
					console.log('The user has not been authenticated on Centric Drive');
				}).then(function() {
					const user = response.getNodeOfType("User");
					if (user && user.UserType != "UserType:User") {
						self._showError({message: locale.resource("ServiceAccountCannotLogin")});
					} else {
						document.location.replace(csi.url("home.html") + "#" + hash());
					}
				});

			}

		},

		_onInitError: function(err) {
			html.setDisabled(this.loginButton, true);
			this._showError(err, true);
		},

		_onError: function(err) {
			this._showError(err, false);
		},

		_showError: function(err, bDisabled) {
			// summary: show error message in login form - any unhandled exceptions encountered during login are treated as if server is unresponsive
			// to make errors during system startup consistent to the user
			var message = err.message;

			if (err.csiErrorCode == "SRV_ERR_SiteAdmin_UNHANDLED_EXCEPTION") {
				message = locale.resource("SERVER_UNRESPONSIVE");
			} else if (err.csiErrorCode == "SRV_ERR_SiteAdmin_PASSWORD_RESET_ERROR") {
				message = locale.resource("UserInactive");
			} else if (err.csiErrorCode == "SRV_ERR_SiteAdmin_INVALID_CREDENTIALS") {
				message = locale.resource("InvalidUserCredentials");
			}

			this._refresh(message, ERROR_MESSAGE_STYLE, bDisabled);

			showPageContent();
		},

		_getRedirectError: function() {
			// summary: gets error message from 'err' query parameter
			// attempts to load a locale resource, otherwise returns given value.
			// Used in instances when server wants to redirect to the login page with an error
			// such as SSO authentication failure.
			var errorMessage = null;
			var uriQuery = window.location.search;

			if (uriQuery) {
				var queryObj = ioQuery.queryToObject(uriQuery.substring(1));
				if (queryObj && queryObj.err) {
					errorMessage = locale.resource(queryObj.err) || queryObj.err;
				}
			}

			return errorMessage;
		},

		_showRedirectError: function() {
			// summary: displays an error in the login form if login.html
			// is redirected to with a query parameter 'err'
			this._showError({message: this.redirectError});
		},

		_preLogin: function( /*Array of form values*/ formValues) {
			_.each(this.authenticationProviders, function(provider) {
				provider.preLogin(formValues);
			});
		},

		_refresh: function(/*String*/message, /*String?*/msgStyle, /*Boolean*/bDisabled) {
			// summary: display login message
			domClass.replace(this.msgContainerNode, msgStyle || NO_MESSAGE_STYLE, ALL_MESSAGE_STYLES);

			if (message) {
				this.messageNode.set("icon", msgStyle == INFO_MESSAGE_STYLE ? this.messageNode.REFRESH : this.messageNode.ERROR);
				this.messageNode.set("value", message);
				html.setVisible(this.messageNode, true);
			}

			domAttr.set(this.loginNode, "autocomplete", "on");

			_.each(this.getFormFields(), function(node) {
				html.setDisabled(node, bDisabled);
			});
		},

		getLoginIdFormField: function() {
			return dom.byId(LOGIN_ID);
		},

		getFieldIds: function() {
			return this.fieldIds.concat(LOGIN_ID);
		},

		getFormFields: function() {
			return _.map(this.getFieldIds(), function(id) {
				return dom.byId(id);
			});
		},

		/**
		* Set the button label based on the view
		* @param {string} label - the button label
		*/
		setLoginButtonLabel: function(label) {
			domAttr.set(LOGIN_BUTTON_LABEL, "textContent", locale.resource(label));
		}
	});

});